/**
 * "Can the BUYER confirm receipt of this order?" — the rule, plus the legacy
 * status ALIAS map it has to run first. Pure, dependency-free, and here rather
 * than in either app because BOTH ask the question about the same rows and the
 * same endpoint answers them: `POST /api/orders/mark-received`.
 *
 * Before 2026-08-04 the rule had five homes across the monorepo — three on web
 * (two of which had silently died, see `lib/orders/can-mark-received.ts` for
 * that autopsy) and two on mobile, which derived it from an ORDER-level
 * rollup instead of the rows the route actually transitions. The rollup is
 * stricter: `computeOverallStatus` reports `refunded`/`cancelled` whenever ANY
 * seller_order is in that state, so a legacy multi-seller order holding one
 * `shipped` row and one `refunded` row hid a CTA the server would have
 * honoured. New orders cannot reach that shape (single-seller invariant), old
 * ones can — and the divergence was invisible because each side looked
 * self-consistent.
 *
 * @see app/api/orders/mark-received/route.ts — the server side of this rule
 * @see lib/orders/decide-order-status-transition.ts — the write-side FSM
 * @see .tondocs/payments/SINGLE_SELLER_INVARIANT_SPEC.md — why new orders are 1:1
 */

import type { SellerOrderStatus } from "./orders-update-status";
import { parseSellerOrderStatus } from "./order-display-status";

/**
 * Collapse the legacy status aliases onto the canonical vocabulary.
 *
 * `orders` / `seller_orders` rows written before the vocabulary settled still
 * carry `received` / `created` / `sent` / `completed` / `in_progress`, none of
 * which are in {@link SELLER_ORDER_STATUSES}.
 *
 * Output is a bare `string`, deliberately: the aliases collapse INTO the union
 * but an unknown value passes through unchanged, so each caller picks its own
 * degradation (web badges want `null`, mobile's list filters want `pending`).
 * Empty / nullish input is the one exception — it reads as `pending`, which is
 * what an order with no status yet is.
 */
export function normalizeSellerOrderStatusAlias(raw?: string | null): string {
  const s = (raw || "").toLowerCase();
  if (s === "received" || s === "created") return "pending";
  if (s === "sent") return "shipped";
  if (s === "completed") return "delivered";
  if (s === "in_progress") return "processing";
  return s || "pending";
}

/**
 * Alias-collapsing total parse into the union. `null` = not a known status.
 *
 * Alias-collapse then the SAME union parse every badge crosses
 * ({@link parseSellerOrderStatus}, `./order-display-status`) — one declaration
 * of "is this string a `SellerOrderStatus`", not two.
 */
export function toSellerOrderStatus(raw?: string | null): SellerOrderStatus | null {
  return parseSellerOrderStatus(normalizeSellerOrderStatusAlias(raw));
}

/**
 * Raw `seller_orders.status` values for every seller_order on the order —
 * legacy aliases and unknown junk welcome, both are handled here.
 *
 * Mirrors the route: `POST /api/orders/mark-received` asks the FSM per row and
 * applies to the `shipped` ones, so the CTA must appear exactly when at least
 * one row would transition. `every(delivered)` needs no separate branch — a
 * `shipped` row makes it false by construction. Feed it the ROWS, never an
 * order-level rollup: the rollup answers a different question (see header).
 *
 * Note this is the MARKETPLACE axis only. `tracking_status = 'delivered'` does
 * NOT enable or disable the CTA: carrier DELIVERED deliberately never promotes
 * the order (buyer agency by design — the buyer confirms, the carrier does not
 * confirm for them), which is the whole reason this button has to exist.
 */
export function canBuyerMarkReceived(
  rawSellerOrderStatuses: readonly (string | null | undefined)[],
): boolean {
  return rawSellerOrderStatuses.some((raw) => toSellerOrderStatus(raw) === "shipped");
}
